import { Button } from "@loot-os/ui/components/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@loot-os/ui/components/card";
import { Skeleton } from "@loot-os/ui/components/skeleton";
import { toast } from "@loot-os/ui/components/sonner";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@loot-os/ui/components/table";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useParams } from "@tanstack/react-router";
import { useState } from "react";

import { FetchStatusBadge } from "../../components/badges";
import { ErrorAlert } from "../../components/ErrorAlert";
import { PayloadSections } from "../../components/PayloadSections";
import { PromoteListingDialog } from "../../components/PromoteListingDialog";
import { centsToDollars, dashIfEmpty, formatDate } from "../../lib/format";
import { useTRPC } from "../../lib/trpc";

// A fetch takes about a minute; poll gently while the row is still PENDING.
// ponytail: no poll deadline — a row stuck PENDING polls until the tab is closed.
// Add one (a mount timestamp plus a 5 minute cut-off) if that ever costs anything.
const POLL_INTERVAL_MS = 5_000;

/**
 * The product-fetch row as this page reads it. The core types `payload` as a
 * recursive JsonObject, and tsc gives up ("excessively deep") the moment this
 * component reads through it; the panel only stringifies each section anyway.
 */
type FetchRow = {
  status: string;
  updatedAt: Date | string;
  errorCode: string | null;
  errorMessage: string | null;
  payload: Record<string, unknown> | null;
};

type StockxProduct = {
  title: string | null;
  brand: string | null;
  styleId: string | null;
  productType: string;
  colorway: string | null;
  color: string | null;
  gender: string | null;
  season: string | null;
  releaseDate: string | null;
  retailPrice: number | null;
  urlKey: string;
  productId: string;
};

const IDENTITY_FIELDS = [
  ["Title", (p: StockxProduct) => p.title],
  ["Brand", (p: StockxProduct) => p.brand],
  ["Style id", (p: StockxProduct) => p.styleId],
  ["Type", (p: StockxProduct) => p.productType],
  ["Colorway", (p: StockxProduct) => p.colorway],
  ["Color", (p: StockxProduct) => p.color],
  ["Gender", (p: StockxProduct) => p.gender],
  ["Season", (p: StockxProduct) => p.season],
  ["Released", (p: StockxProduct) => p.releaseDate],
  ["Retail", (p: StockxProduct) => p.retailPrice],
  ["Url key", (p: StockxProduct) => p.urlKey],
  ["Product id", (p: StockxProduct) => p.productId],
] as const;

const MARKET_COLUMNS = [
  ["Lowest ask", "lowestAskAmount"],
  ["Highest bid", "highestBidAmount"],
  ["Sell faster", "sellFasterAmount"],
  ["Earn more", "earnMoreAmount"],
  ["Flex ask", "flexLowestAskAmount"],
] as const;

/** StockX product detail: identity, variants with market levels, and the deep fetch. */
export function StockxProductPage() {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const { productId } = useParams({ from: "/app/fulfillment/stockx/$productId" });

  const product = useQuery(trpc.platform.fulfillment.stockx.product.queryOptions({ productId }));
  const variants = useQuery(trpc.platform.fulfillment.stockx.variants.queryOptions({ productId }));
  const market = useQuery(trpc.platform.fulfillment.stockx.marketData.queryOptions({ productId }));

  const urlKey = product.data?.product.urlKey;
  const fetchInput = { vendorProductKey: urlKey ?? "" };
  const productFetch = useQuery(
    trpc.platform.fulfillment.productFetch.get.queryOptions(fetchInput, {
      enabled: !!urlKey,
      refetchInterval: (query) =>
        (query.state.data?.fetch as FetchRow | null | undefined)?.status === "PENDING"
          ? POLL_INTERVAL_MS
          : false,
    }),
  );

  const [isPromoteOpen, setIsPromoteOpen] = useState(false);
  const promote = useMutation(
    trpc.platform.fulfillment.productFetch.promote.mutationOptions({
      onSuccess: ({ listings, skippedTenantIds }) => {
        setIsPromoteOpen(false);
        toast.success(
          `Wrote ${listings.length} listing ${listings.length === 1 ? "row" : "rows"}` +
            (skippedTenantIds.length > 0
              ? `; skipped unknown tenants ${skippedTenantIds.join(", ")}`
              : ""),
        );
      },
    }),
  );

  const start = useMutation(
    trpc.platform.fulfillment.productFetch.start.mutationOptions({
      // A new fetch invalidates the promotion result printed below the buttons.
      onMutate: () => promote.reset(),
      onSuccess: () => {
        void queryClient.invalidateQueries({
          queryKey: trpc.platform.fulfillment.productFetch.get.queryKey(fetchInput),
        });
      },
    }),
  );

  const row = productFetch.data?.fetch as FetchRow | null | undefined;
  const isPolling = row?.status === "PENDING";
  // The server decides promotability, so the button cannot offer a row `promote` will refuse.
  const canPromote = productFetch.data?.isPromotable === true;
  const byVariant = new Map(
    (market.data?.marketData ?? []).map((entry) => [entry.variantId, entry]),
  );

  return (
    <div className="flex flex-col gap-6">
      <div>
        <Link className="text-sm text-muted-foreground underline" to="/fulfillment/stockx">
          ← StockX catalog
        </Link>
        <h1 className="text-2xl font-semibold tracking-tight">
          {product.data?.product.title ?? productId}
        </h1>
      </div>

      {product.error && <ErrorAlert message={product.error.message} />}

      <Card>
        <CardHeader>
          <CardTitle>Product</CardTitle>
        </CardHeader>
        <CardContent>
          {product.isLoading ? (
            <Skeleton className="h-64 w-full" />
          ) : product.data ? (
            <Table>
              <TableBody>
                {IDENTITY_FIELDS.map(([label, read]) => (
                  <TableRow key={label}>
                    <TableHead className="w-40">{label}</TableHead>
                    <TableCell>{dashIfEmpty(read(product.data.product))}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <p className="text-sm text-muted-foreground">No product for this id.</p>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Deep fetch</CardTitle>
          <CardDescription>
            The paid scrape that fills the product-fetch cache. It takes about a minute; this page
            polls while it runs.
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-4">
          <div className="flex flex-wrap items-center gap-3">
            <FetchStatusBadge status={row?.status ?? "NONE"} />
            <span className="text-sm text-muted-foreground">
              {row ? `updated ${formatDate(row.updatedAt)}` : "No fetch cached for this product."}
            </span>
            <Button
              type="button"
              disabled={!urlKey || start.isPending || promote.isPending || isPolling}
              onClick={() => start.mutate({ ...fetchInput, force: true })}
            >
              {start.isPending ? "Starting…" : isPolling ? "Fetching…" : "Fetch market data"}
            </Button>
            <PromoteListingDialog
              open={isPromoteOpen}
              disabled={!canPromote || start.isPending}
              isPending={promote.isPending}
              onOpenChange={setIsPromoteOpen}
              onPromote={(input) => promote.mutate({ ...fetchInput, ...input })}
            />
          </div>
          {row?.errorCode && <ErrorAlert message={`${row.errorCode}: ${row.errorMessage ?? ""}`} />}
          {/* The route component is reused across product ids; only this product's result shows. */}
          {promote.data && promote.variables?.vendorProductKey === urlKey && (
            <ul className="text-sm text-muted-foreground">
              {promote.data.listings.map((listing) => (
                <li key={listing.id}>
                  Listed as <code>{listing.id}</code> at {centsToDollars(listing.price)} with{" "}
                  {listing.variants?.length ?? 0} variants listed.
                </li>
              ))}
            </ul>
          )}
          <PayloadSections payload={row?.payload} />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Variants and market data</CardTitle>
          <CardDescription>
            {variants.data?.variants.length ?? 0} variants · amounts as StockX sends them
          </CardDescription>
        </CardHeader>
        <CardContent>
          {variants.error && <ErrorAlert message={variants.error.message} />}
          {variants.isLoading ? (
            <Skeleton className="h-48 w-full" />
          ) : (variants.data?.variants.length ?? 0) === 0 ? (
            <p className="text-sm text-muted-foreground">No variants for this product.</p>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Variant</TableHead>
                    <TableHead>Value</TableHead>
                    <TableHead>Sizes</TableHead>
                    {MARKET_COLUMNS.map(([label]) => (
                      <TableHead key={label}>{label}</TableHead>
                    ))}
                    <TableHead>Currency</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(variants.data?.variants ?? []).map((variant) => {
                    const levels = byVariant.get(variant.variantId);
                    return (
                      <TableRow key={variant.variantId}>
                        <TableCell>{dashIfEmpty(variant.variantName)}</TableCell>
                        <TableCell>{dashIfEmpty(variant.variantValue)}</TableCell>
                        <TableCell>
                          {dashIfEmpty(
                            Object.entries(variant.sizes)
                              .map(([type, size]) => `${type} ${size}`)
                              .join(", "),
                          )}
                        </TableCell>
                        {MARKET_COLUMNS.map(([label, field]) => (
                          <TableCell key={label}>{dashIfEmpty(levels?.[field])}</TableCell>
                        ))}
                        <TableCell>{dashIfEmpty(levels?.currencyCode)}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
